import { cloneElement, ReactElement, ReactNode } from 'react';

import {
  StatusToastApiOptions,
  StatusToastCallOptions,
  StatusToastController,
  StatusToastPromiseOption,
} from './statusToastController';
import { renderStatusToast } from '../components/StatusToast/renderStatusToast';
import { StatusToastProps } from '../components/StatusToast/StatusToast';
import {
  STATUS_TOAST_DURATION_MS,
  StatusToastVariant,
  ToastPosition,
} from '../constants/statusToast';

const TOASTER_CONTAINER_MARGIN_BOTTOM = '20px';
const TOASTER_CONTAINER_MARGIN_LEFT = '10px';

function buildStatusToastOptions(
  status: StatusToastVariant,
  message: string,
  position: ToastPosition,
  title?: string,
  action?: StatusToastProps['action'],
): StatusToastCallOptions {
  return {
    duration: STATUS_TOAST_DURATION_MS,
    isClosable: true,
    position,
    render: ({ onClose }) =>
      renderStatusToast({
        status,
        title,
        message,
        action,
        onClose,
        durationMs: STATUS_TOAST_DURATION_MS,
      }),
  };
}

function buildPromiseStatusOption(
  status: StatusToastVariant,
  message: string,
  position: ToastPosition,
): StatusToastPromiseOption {
  if (status === StatusToastVariant.Loading) {
    return {
      position,
      render: () => renderStatusToast({ status, message }),
    };
  }

  return {
    position,
    duration: STATUS_TOAST_DURATION_MS,
    isClosable: true,
    render: ({ onClose }) =>
      renderStatusToast({
        status,
        message,
        onClose,
        durationMs: STATUS_TOAST_DURATION_MS,
      }),
  };
}

function createPromiseToast(
  toast: StatusToastController,
  defaultPosition: ToastPosition,
) {
  return (
    promise: Promise<unknown>,
    loadingMsg: string,
    successMsg: string,
    errorMsg: string | ((err: unknown) => string),
    position: ToastPosition = defaultPosition,
  ) => {
    let resolvedErrorMsg = typeof errorMsg === 'string' ? errorMsg : '';

    const wrappedPromise = promise.catch((err) => {
      if (typeof errorMsg === 'function') {
        resolvedErrorMsg = errorMsg(err);
      }
      throw err;
    });

    toast.promise(wrappedPromise, {
      loading: buildPromiseStatusOption(
        StatusToastVariant.Loading,
        loadingMsg,
        position,
      ),
      success: buildPromiseStatusOption(
        StatusToastVariant.Success,
        successMsg,
        position,
      ),
      error: () =>
        buildPromiseStatusOption(
          StatusToastVariant.Error,
          resolvedErrorMsg,
          position,
        ),
    });

    return wrappedPromise;
  };
}

function createCustomToaster(
  toast: StatusToastController,
  defaultPosition: ToastPosition,
) {
  return (
    toasterElement: ReactNode,
    position: ToastPosition = defaultPosition,
    duration = STATUS_TOAST_DURATION_MS,
  ) => {
    return toast({
      position,
      duration,
      isClosable: true,
      containerStyle: {
        marginBottom: TOASTER_CONTAINER_MARGIN_BOTTOM,
        marginLeft: TOASTER_CONTAINER_MARGIN_LEFT,
      },
      render: ({ onClose }) =>
        cloneElement(toasterElement as ReactElement<{ onClose: () => void }>, {
          onClose,
        }),
    });
  };
}

export function createStatusToastApi(
  toast: StatusToastController,
  { defaultPosition }: StatusToastApiOptions,
) {
  const successMessage = (
    description: string,
    title?: string,
    position: ToastPosition = defaultPosition,
    action?: StatusToastProps['action'],
  ) => {
    toast(
      buildStatusToastOptions(
        StatusToastVariant.Success,
        description,
        position,
        title,
        action,
      ),
    );
  };

  const errorMessage = (
    description: string,
    title?: string,
    position: ToastPosition = defaultPosition,
    action?: StatusToastProps['action'],
  ) => {
    toast(
      buildStatusToastOptions(
        StatusToastVariant.Error,
        description,
        position,
        title,
        action,
      ),
    );
  };

  const warningMessage = (
    description: string,
    title?: string,
    position: ToastPosition = defaultPosition,
    action?: StatusToastProps['action'],
  ) => {
    toast(
      buildStatusToastOptions(
        StatusToastVariant.Warning,
        description,
        position,
        title,
        action,
      ),
    );
  };

  return {
    successMessage,
    errorMessage,
    warningMessage,
    promiseToast: createPromiseToast(toast, defaultPosition),
    customToaster: createCustomToaster(toast, defaultPosition),
    closeAllToasts: toast.closeAll,
    closeToast: toast.close,
  };
}

export type StatusToastApi = ReturnType<typeof createStatusToastApi>;
